import { useState } from 'react';
import { Plus, Trash2, User, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import type {
  OpportunityRole,
  OpportunityRoleType,
  CreateRoleDto,
} from '../../types/role.types';
import { ROLE_CONFIG } from '../../types/role.types';

interface RolesPanelProps {
  opportunityId: number;
  roles: OpportunityRole[];
  users: { id: number; name: string; email: string }[];
  isLoading?: boolean;
  onAddRole: (dto: CreateRoleDto) => void;
  onRemoveRole: (roleId: number) => void;
}

const ROLE_TYPES: OpportunityRoleType[] = [
  'coordinator',
  'contributor',
  'reviewer',
  'observer',
  'lot_manager',
];

export function RolesPanel({
  opportunityId,
  roles,
  users,
  isLoading,
  onAddRole,
  onRemoveRole,
}: RolesPanelProps) {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [deleteRoleId, setDeleteRoleId] = useState<number | null>(null);
  const [newRole, setNewRole] = useState<{
    userId: string;
    role: OpportunityRoleType;
    notes: string;
  }>({
    userId: '',
    role: 'contributor',
    notes: '',
  });

  const coordinator = roles.find((r) => r.role === 'coordinator');
  const otherRoles = roles.filter((r) => r.role !== 'coordinator');

  // Utilisateurs non encore assignés
  const assignedUserIds = new Set(roles.map((r) => r.userId));
  const availableUsers = users.filter((u) => !assignedUserIds.has(u.id));

  const handleAddRole = () => {
    if (!newRole.userId) return;

    onAddRole({
      opportunityId,
      userId: parseInt(newRole.userId, 10),
      role: newRole.role,
      notes: newRole.notes || undefined,
    });

    setShowAddDialog(false);
    setNewRole({ userId: '', role: 'contributor', notes: '' });
  };

  const handleDeleteConfirm = () => {
    if (deleteRoleId) {
      onRemoveRole(deleteRoleId);
      setDeleteRoleId(null);
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
          <CardTitle className="text-lg">Équipe</CardTitle>
          <Button
            size="sm"
            onClick={() => setShowAddDialog(true)}
            disabled={isLoading || availableUsers.length === 0}
          >
            <Plus className="h-4 w-4 mr-1" />
            Ajouter
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Coordinateur */}
          {coordinator ? (
            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-100 rounded-full">
                  <Crown className="h-4 w-4 text-purple-700" />
                </div>
                <div>
                  <p className="font-medium">{coordinator.userName || 'Utilisateur'}</p>
                  <p className="text-sm text-muted-foreground">
                    {coordinator.userEmail}
                  </p>
                </div>
              </div>
              <Badge className="bg-purple-100 text-purple-700">
                Coordinateur
              </Badge>
            </div>
          ) : (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-amber-800 text-sm">
              Aucun coordinateur assigné. Ajoutez un coordinateur pour cette opportunité.
            </div>
          )}

          {/* Autres rôles */}
          {otherRoles.length > 0 ? (
            <div className="space-y-2">
              {otherRoles.map((role) => {
                const config = ROLE_CONFIG[role.role];
                return (
                  <div
                    key={role.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-background rounded-full">
                        <User className="h-4 w-4 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="font-medium">
                          {role.userName || 'Utilisateur'}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {role.userEmail}
                        </p>
                        {role.notes && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {role.notes}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={`${config.bgColor} ${config.color}`}>
                        {config.label}
                      </Badge>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        onClick={() => setDeleteRoleId(role.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            !coordinator && (
              <p className="text-sm text-muted-foreground text-center py-4">
                Aucun membre assigné à cette opportunité.
              </p>
            )
          )}
        </CardContent>
      </Card>

      {/* Dialog d'ajout */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un membre</DialogTitle>
            <DialogDescription>
              Assignez un utilisateur à cette opportunité avec un rôle spécifique.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Utilisateur</Label>
              <Select
                value={newRole.userId}
                onValueChange={(value) =>
                  setNewRole((prev) => ({ ...prev, userId: value }))
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner un utilisateur" />
                </SelectTrigger>
                <SelectContent>
                  {availableUsers.map((user) => (
                    <SelectItem key={user.id} value={String(user.id)}>
                      {user.name} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Rôle</Label>
              <Select
                value={newRole.role}
                onValueChange={(value) =>
                  setNewRole((prev) => ({
                    ...prev,
                    role: value as OpportunityRoleType,
                  }))
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_TYPES.map((roleType) => {
                    const config = ROLE_CONFIG[roleType];
                    const isCoordinatorDisabled =
                      roleType === 'coordinator' && !!coordinator;
                    return (
                      <SelectItem
                        key={roleType}
                        value={roleType}
                        disabled={isCoordinatorDisabled}
                      >
                        <div className="flex items-center gap-2">
                          <span>{config.label}</span>
                          {isCoordinatorDisabled && (
                            <span className="text-xs text-muted-foreground">
                              (déjà assigné)
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {ROLE_CONFIG[newRole.role].description}
              </p>
            </div>
            <div className="space-y-2">
              <Label>Notes (optionnel)</Label>
              <Textarea
                placeholder="Informations complémentaires..."
                value={newRole.notes}
                onChange={(e) =>
                  setNewRole((prev) => ({ ...prev, notes: e.target.value }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleAddRole} disabled={!newRole.userId}>
              Ajouter
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de suppression */}
      <AlertDialog
        open={deleteRoleId !== null}
        onOpenChange={() => setDeleteRoleId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Retirer ce membre ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette personne n'aura plus accès à cette opportunité.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteConfirm}>
              Retirer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
